import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Navbar } from "./navbar";
import { User, Mail, Lock, ShieldCheck, Rocket, Hand, AlertTriangle, ArrowLeft } from "lucide-react";
import { getDataComptes, addCompte, login, register, firebaseSync, sendAuthCode, verifyAuthCode, verifyRegister } from "./action";
import { auth, googleProvider } from "../firebase";
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, signInWithPopup, sendEmailVerification, sendPasswordResetEmail } from "firebase/auth";
import "../styles/login.css";

export function Login() {
    const [IsLogin, setIsLogin] = useState(true);
    const [Name, setName] = useState("");
    const [Email, setEmail] = useState("");
    const [Password, setPassword] = useState("");
    const [ConfirmPS, setConfirmPS] = useState("");
    const [error, setError] = useState("");
    const [ShowReset, setShowReset] = useState(false);
    const [ResetStep, setResetStep] = useState(1); // 1: Email, 2: Code, 3: New Passwords
    const [ResetCode, setResetCode] = useState("");
    const [NewPassword, setNewPassword] = useState("");
    const [ConfirmNewPassword, setConfirmNewPassword] = useState("");
    const [VerifyRegister, setVerifyRegister] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [shakeName, setShakeName] = useState(false);
    const [shakeEmail, setShakeEmail] = useState(false);
    const [shakePassword, setShakePassword] = useState(false);
    const [shakeConfirmPassword, setShakeConfirmPassword] = useState(false);
    const [googleToken, setGoogleToken] = useState(null);
    const [isGoogleRegistration, setIsGoogleRegistration] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const getErrorMsg = (err) => {
        if (!err) return "";
        return Array.isArray(err) ? err[0] : err;
    };
    const [registerCode, setRegisterCode] = useState("");
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const comptes = useSelector((state) => state.comptes) || [];

    useEffect(() => {
        dispatch(getDataComptes());
    }, [dispatch]);

    const handleGoogleLogin = async () => {
        if (isLoading) return;
        setIsLoading(true);
        setError("");
        try {
            const result = await signInWithPopup(auth, googleProvider);
            const token = await result.user.getIdToken();

            dispatch(firebaseSync(token))
                .unwrap()
                .then((data) => {
                    const { user, token: firebaseToken } = data; // Assuming sync returns both if updated
                    localStorage.removeItem('user');

                    const userPayload = JSON.stringify({
                        id: user.id,
                        name: user.username,
                        email: user.email,
                        role: user.role.toLowerCase(),
                        profilePic: user.image_user?.length < 100000 ? user.image_user : null,
                        isGoogle: true
                    });

                    try {
                        localStorage.setItem('user', userPayload);
                    } catch (e) {
                        localStorage.clear();
                        localStorage.setItem('user', JSON.stringify({
                            id: user.id,
                            name: user.username,
                            email: user.email,
                            role: user.role.toLowerCase(),
                            profilePic: null,
                            isGoogle: true
                        }));
                    }
                    localStorage.setItem('firebase_token', token);
                    setIsLoading(false);
                    if (!IsLogin) {
                        alert("Ce compte existe déjà. Vous avez été connecté.");
                    } else {
                        alert("Connexion Google réussie !");
                    }
                    if (user.role.toLowerCase() === 'admin' || user.role.toLowerCase() === 'super admin') {
                        navigate('/dashbordPage');
                    } else {
                        navigate("/");
                    }
                })
                .catch(async (err) => {
                    if (err.status === 428 || err.message === 'Verification code required for registration') {
                        // Registration requires verification code
                        try {
                            const userEmail = result.user.email?.trim().toLowerCase();
                            await dispatch(sendAuthCode(userEmail)).unwrap();
                            setGoogleToken(token);
                            setIsGoogleRegistration(true);
                            setEmail(userEmail);
                            setName(result.user.displayName || "Utilisateur Google");
                            setError("");
                            setVerifyRegister(true);
                            setIsLoading(false);
                            alert("Un code de vérification a été envoyé à " + userEmail);
                        } catch (sendErr) {
                            setError("Erreur lors de l'envoi du code: " + getErrorMsg(sendErr));
                            setIsLoading(false);
                        }
                    } else {
                        setError(err.message || "Erreur de synchronisation Firebase");
                        setIsLoading(false);
                    }
                });
        } catch (err) {
            setError("Erreur de connexion Google: " + err.message);
            setIsLoading(false);
        }
    };

    const handleInitiateReset = () => {
        setShowReset(true);
        setResetStep(1);
        setError("");
    };

    const handleSendResetCode = async (e) => {
        e.preventDefault();
        if (!Email) {
            setError("Veuillez entrer votre email.");
            return;
        }
        try {
            await dispatch(sendAuthCode(Email)).unwrap();
            setResetStep(2);
            alert("Un code a été envoyé à " + Email);
        } catch (err) {
            setError("Erreur: " + err.message);
        }
    };

    const handleVerifyResetCode = (e) => {
        e.preventDefault();
        if (!ResetCode) {
            setError("Veuillez entrer le code reçu.");
            return;
        }
        setResetStep(3);
        setError("");
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        if (!ResetCode || !NewPassword || !ConfirmNewPassword) {
            setError("Veuillez remplir tous les champs.");
            return;
        }
        if (NewPassword !== ConfirmNewPassword) {
            setError("Les mots de passe ne correspondent pas.");
            return;
        }
        try {
            await dispatch(verifyAuthCode({
                email: Email,
                code: ResetCode,
                new_password: NewPassword
            })).unwrap();
            alert("Mot de passe réinitialisé !");
            setShowReset(false);
            setResetStep(1);
            setPassword("");
        } catch (err) {
            setError(err.message || "Code incorrect.");
        }
    };

    const handleVerifyAndRegister = async (e) => {
        e.preventDefault();
        setError("");
        setValidationErrors({});
        if (!registerCode) {
            setError("Veuillez entrer le code.");
            return;
        }
        try {
            const emailKey = Email.trim().toLowerCase();

            if (isGoogleRegistration) {
                // 1. Sync session with local database (using token + code)
                const syncData = await dispatch(firebaseSync({ token: googleToken, code: registerCode.trim() })).unwrap();
                const user = syncData.user;
                localStorage.removeItem('user');

                const userPayload = JSON.stringify({
                    id: user.id,
                    name: user.username,
                    email: user.email,
                    role: user.role.toLowerCase(),
                    profilePic: user.image_user?.length < 100000 ? user.image_user : null,
                    isGoogle: true
                });

                try {
                    localStorage.setItem('user', userPayload);
                } catch (e) {
                    localStorage.clear();
                    localStorage.setItem('user', JSON.stringify({
                        id: user.id,
                        name: user.username,
                        email: user.email,
                        role: user.role.toLowerCase(),
                        profilePic: null,
                        isGoogle: true
                    }));
                }
                localStorage.setItem('firebase_token', googleToken);

                alert("Compte Google vérifié et créé avec succès !");
                setVerifyRegister(false);
                setIsGoogleRegistration(false);
                setGoogleToken(null);
                setRegisterCode("");

                if (user.role.toLowerCase() === 'admin') {
                    navigate('/dashbordPage');
                } else {
                    navigate("/");
                }
                return;
            }

            // Normal email/password registration flow
            // 1. Verify and register in the database backend
            await dispatch(verifyRegister({
                username: Name.trim(),
                email: emailKey,
                password: Password,
                code: registerCode.trim()
            })).unwrap();

            // 2. Automatically log the user in via Firebase Auth
            const userCredential = await signInWithEmailAndPassword(auth, emailKey, Password);
            const token = await userCredential.user.getIdToken();

            // 3. Sync session with local database
            const syncData = await dispatch(firebaseSync(token)).unwrap();
            const user = syncData.user;

            localStorage.removeItem('user');

            const userPayload = JSON.stringify({
                id: user.id,
                name: user.username,
                email: user.email,
                role: user.role.toLowerCase(),
                profilePic: user.image_user?.length < 100000 ? user.image_user : null
            });

            try {
                localStorage.setItem('user', userPayload);
            } catch (e) {
                localStorage.clear();
                localStorage.setItem('user', JSON.stringify({
                    id: user.id,
                    name: user.username,
                    email: user.email,
                    role: user.role.toLowerCase(),
                    profilePic: null
                }));
            }
            localStorage.setItem('firebase_token', token);

            alert("Compte créé et connecté avec succès !");
            setVerifyRegister(false);
            setPassword("");
            setRegisterCode("");

            if (user.role.toLowerCase() === 'admin') {
                navigate('/dashbordPage');
            } else {
                navigate("/");
            }
        } catch (err) {
            if (err && err.errors) {
                setValidationErrors(err.errors);
                setVerifyRegister(false);
                setError("Veuillez corriger les champs indiqués.");
                if (err.errors.username) setShakeName(true);
                if (err.errors.email) setShakeEmail(true);
                if (err.errors.password) setShakePassword(true);
                setTimeout(() => {
                    setShakeName(false);
                    setShakeEmail(false);
                    setShakePassword(false);
                }, 400);
            } else if (err?.errors?.email) {
                setValidationErrors({ email: "Cette adresse email est déjà utilisée." });
                setVerifyRegister(false);
                setShakeEmail(true);
                setTimeout(() => setShakeEmail(false), 400);
            } else {
                setError("Création du compte échouée. Veuillez réessayer.");
            }
        }
    };

    function handleSubmit(e) {
        e.preventDefault();
        setError("");
        setValidationErrors({});

        const emailKey = Email.trim().toLowerCase();

        if (IsLogin) {
            // First check if email exists in local accounts list
            const userExists = comptes.some(c => c.email && c.email.trim().toLowerCase() === emailKey);
            if (!userExists) {
                setValidationErrors({ email: "Aucun compte n'existe avec cet email. Redirection vers l'inscription..." });
                setShakeEmail(true);
                setTimeout(() => setShakeEmail(false), 400);
                setTimeout(() => {
                    setIsLogin(false);
                    setValidationErrors({});
                }, 2500);
                return;
            }

            signInWithEmailAndPassword(auth, emailKey, Password)
                .then(async (userCredential) => {
                    const token = await userCredential.user.getIdToken();
                    dispatch(firebaseSync(token))
                        .unwrap()
                        .then((data) => {
                            const user = data.user;
                            localStorage.removeItem('user');

                            const userPayload = JSON.stringify({
                                id: user.id,
                                name: user.username,
                                email: user.email,
                                role: user.role.toLowerCase(),
                                profilePic: user.image_user?.length < 100000 ? user.image_user : null
                            });

                            try {
                                localStorage.setItem('user', userPayload);
                            } catch (e) {
                                localStorage.clear();
                                localStorage.setItem('user', JSON.stringify({
                                    id: user.id,
                                    name: user.username,
                                    email: user.email,
                                    role: user.role.toLowerCase(),
                                    profilePic: null
                                }));
                            }
                            localStorage.setItem('firebase_token', token);
                            if (user.role.toLowerCase() === 'admin' || user.role.toLowerCase() === 'super admin') {
                                navigate('/dashbordPage');
                            } else {
                                navigate("/");
                            }
                        })
                        .catch((err) => {
                            if (err?.status === 404 || err?.message?.includes('not found')) {
                                setValidationErrors({ email: "Aucun compte local trouvé. Contactez le support." });
                                setShakeEmail(true);
                                setTimeout(() => setShakeEmail(false), 400);
                            } else {
                                setError("Connexion impossible. Veuillez réessayer.");
                            }
                        });
                })
                .catch((err) => {
                    const code = err?.code;
                    if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
                        setValidationErrors({ password: "Mot de passe incorrect" });
                        setShakePassword(true);
                        setTimeout(() => setShakePassword(false), 400);
                    } else if (code === 'auth/too-many-requests') {
                        setError("Trop de tentatives. Réessayez dans quelques minutes.");
                    } else if (code === 'auth/network-request-failed') {
                        setError("Problème de connexion internet. Vérifiez votre réseau.");
                    } else {
                        setError("Connexion impossible. Veuillez réessayer.");
                    }
                });
        } else {
            let localErrors = {};

            // Username validation (Only letters and spaces)
            const fullNameRegex = /^[a-zA-ZÀ-ÿ\s]+$/;
            if (!Name.trim()) {
                localErrors.username = "Le nom complet est requis.";
                setShakeName(true);
            } else if (!fullNameRegex.test(Name.trim())) {
                localErrors.username = "Le nom complet ne doit contenir que des lettres et des espaces.";
                setShakeName(true);
            }

            // Email validation
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!Email.trim()) {
                localErrors.email = "L'adresse email est requise.";
                setShakeEmail(true);
            } else if (!emailRegex.test(Email.trim())) {
                localErrors.email = "L'adresse email n'est pas valide.";
                setShakeEmail(true);
            } else {
                const emailExists = comptes.some(c => c.email && c.email.trim().toLowerCase() === emailKey);
                if (emailExists) {
                    localErrors.email = "Cet email est déjà utilisé. Redirection vers la connexion...";
                    setShakeEmail(true);
                    setTimeout(() => {
                        setIsLogin(true);
                        setValidationErrors({});
                    }, 2500);
                }
            }

            // Password validation
            if (!Password) {
                localErrors.password = "Le mot de passe est requis.";
                setShakePassword(true);
            } else {
                const isSecure = Password.length >= 8 &&
                    /[a-z]/.test(Password) &&
                    /[A-Z]/.test(Password) &&
                    /[0-9]/.test(Password) &&
                    /[@$!%*#?&]/.test(Password);

                if (!isSecure) {
                    localErrors.password = "Le mot de passe doit être sécurisé (au moins 8 caractères, des lettres minuscules et majuscules, des chiffres et des caractères spéciaux).";
                    setShakePassword(true);
                }
            }

            if (Password !== ConfirmPS) {
                localErrors.confirmPassword = "Les mots de passe ne correspondent pas.";
                setShakeConfirmPassword(true);
            }

            if (Object.keys(localErrors).length > 0) {
                setValidationErrors(localErrors);
                setTimeout(() => {
                    setShakeName(false);
                    setShakeEmail(false);
                    setShakePassword(false);
                    setShakeConfirmPassword(false);
                }, 400);
                return;
            }

            dispatch(sendAuthCode(emailKey))
                .unwrap()
                .then((res) => {
                    setVerifyRegister(true);
                    if (res && res.isFallback) {
                        alert("⚠️ Serveur SMTP Hors-ligne sur le Cloud. Votre code de test de validation est: " + res.debug_code);
                    } else {
                        alert("Un code de vérification a été envoyé à " + Email);
                    }
                })
                .catch((err) => setError("Erreur: " + err.message));
        }
    }

    return (
        <div className="login-page-container">
            <div className={`login-content-wrapper ${IsLogin ? "is-login" : "is-signup"}`}>
                <div className="login-card-modern">
                    <div className="login-visual-side">
                        <button className="btn-back-home" onClick={() => navigate("/")}>
                            <ArrowLeft size={20} className="back-icon" />
                            <span>Retour à l'accueil</span>
                        </button>
                        <div className="visual-overlay">
                            <h2 className="visual-title">{IsLogin ? "Nouveau ici ?" : "Déjà membre ?"}</h2>
                            <p className="visual-subtitle">
                                {IsLogin
                                    ? "Rejoignez Jamas Lens pour découvrir les meilleures solutions de climatisation pour votre maison."
                                    : "Connectez-vous pour accéder à votre profil, vos commandes et notre support."}
                            </p>
                            <button className="btn-ghost-modern" onClick={() => {
                                setIsLogin(!IsLogin);
                                setVerifyRegister(false);
                                setError("");
                                setValidationErrors({});
                            }}>
                                {IsLogin ? "Créer un compte" : "Se connecter"}
                            </button>
                        </div>
                    </div>

                    <div className="login-form-side-modern">
                        {ShowReset ? (
                            <div className="premium-auth-form">
                                <div className="form-header-modern">
                                    <h2>Réinitialisation</h2>
                                    <p>
                                        {ResetStep === 1 && "Entrez votre email pour recevoir le code."}
                                        {ResetStep === 2 && "Entrez le code envoyé à " + Email}
                                        {ResetStep === 3 && "Choisissez votre nouveau mot de passe."}
                                    </p>
                                </div>

                                {ResetStep === 1 && (
                                    <form onSubmit={handleSendResetCode}>
                                        <div className="premium-input-group">
                                            <label>Email</label>
                                            <div className="input-wrapper">
                                                <span className="input-icon"><Mail size={18} /></span>
                                                <input type="email" value={Email} onChange={(e) => setEmail(e.target.value)} required placeholder="votre@email.com" />
                                            </div>
                                        </div>
                                        {error && <div className="modern-error-msg"><AlertTriangle size={18} /> {error}</div>}
                                        <button type="submit" className="btn-glow-primary">Valider</button>
                                        <button type="button" onClick={() => setShowReset(false)} className="btn-text-link mt-3">Annuler</button>
                                    </form>
                                )}

                                {ResetStep === 2 && (
                                    <form onSubmit={handleVerifyResetCode}>
                                        <div className="premium-input-group">
                                            <label>Email</label>
                                            <div className="input-wrapper">
                                                <span className="input-icon"><Mail size={18} /></span>
                                                <input type="email" value={Email} disabled />
                                            </div>
                                        </div>
                                        <div className="premium-input-group mt-2">
                                            <label>Code de vérification</label>
                                            <div className="input-wrapper">
                                                <span className="input-icon"><ShieldCheck size={18} /></span>
                                                <input type="text" value={ResetCode} onChange={(e) => setResetCode(e.target.value)} required placeholder="Code à 4 chiffres" />
                                            </div>
                                        </div>
                                        {error && <div className="modern-error-msg"><AlertTriangle size={18} /> {error}</div>}
                                        <button type="submit" className="btn-glow-primary">Valider le code</button>
                                        <button type="button" onClick={() => setResetStep(1)} className="btn-text-link mt-3">Changer l'email</button>
                                    </form>
                                )}

                                {ResetStep === 3 && (
                                    <form onSubmit={handleResetPassword}>
                                        <div className="premium-input-group">
                                            <label>Nouveau mot de passe</label>
                                            <div className="input-wrapper">
                                                <span className="input-icon"><Lock size={18} /></span>
                                                <input type="password" value={NewPassword} onChange={(e) => setNewPassword(e.target.value)} required placeholder="••••••••" />
                                            </div>
                                        </div>
                                        <div className="premium-input-group mt-2">
                                            <label>Confirmer le mot de passe</label>
                                            <div className="input-wrapper">
                                                <span className="input-icon"><ShieldCheck size={18} /></span>
                                                <input type="password" value={ConfirmNewPassword} onChange={(e) => setConfirmNewPassword(e.target.value)} required placeholder="••••••••" />
                                            </div>
                                        </div>
                                        {error && <div className="modern-error-msg"><AlertTriangle size={18} /> {error}</div>}
                                        <button type="submit" className="btn-glow-primary">Enregistrer</button>
                                        <button type="button" onClick={() => setShowReset(false)} className="btn-text-link mt-3">Annuler</button>
                                    </form>
                                )}
                            </div>
                        ) : VerifyRegister ? (
                            <form onSubmit={handleVerifyAndRegister} className="premium-auth-form">
                                <div className="form-header-modern">
                                    <h2>Vérification</h2>
                                    <p>Entrez le code envoyé à {Email} pour finaliser votre inscription.</p>
                                </div>
                                <div className="premium-input-group">
                                    <label>Code de vérification</label>
                                    <div className="input-wrapper">
                                        <span className="input-icon"><ShieldCheck size={18} /></span>
                                        <input
                                            type="text"
                                            placeholder="Code à 4 chiffres"
                                            value={registerCode}
                                            onChange={(e) => setRegisterCode(e.target.value)}
                                            required
                                        />
                                    </div>
                                </div>
                                {error && <div className="modern-error-msg"><AlertTriangle className="responsive-icon error-icon" size={18} /> {error}</div>}
                                <button type="submit" className="btn-glow-primary">Valider</button>
                                <button type="button" onClick={() => setVerifyRegister(false)} className="btn-text-link mt-2">Retour</button>
                            </form>
                        ) : (
                            <>
                                <div className="form-header-modern">
                                    <h2>{IsLogin ? <>{IsLogin && "Bon retour ! "} <Hand className="responsive-icon header-icon" size={28} /></> : <><Rocket className="responsive-icon header-icon" size={28} /> Créer un compte</>}</h2>
                                    <p>
                                        {IsLogin
                                            ? "Heureux de vous revoir. Veuillez vous connecter."
                                            : "Remplissez les informations ci-dessous."}
                                    </p>
                                </div>
                                <form onSubmit={handleSubmit} className="premium-auth-form">
                                    {!IsLogin && (
                                        <div className="premium-input-group">
                                            <label>Nom complet</label>
                                            <div className="input-wrapper">
                                                <span className="input-icon"><User size={18} /></span>
                                                <input
                                                    type="text"
                                                    placeholder="Nom et Prénom"
                                                    value={Name}
                                                    onChange={(e) => setName(e.target.value)}
                                                    required
                                                    className={`${(validationErrors.username || validationErrors.Name) ? 'error-border' : ''} ${shakeName ? 'shake-element' : ''}`}
                                                />
                                            </div>
                                            {(validationErrors.username || validationErrors.Name) && <span style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '4px', display: 'block', fontWeight: '500' }}>{getErrorMsg(validationErrors.username || validationErrors.Name)}</span>}
                                        </div>
                                    )}

                                    <div className="premium-input-group">
                                        <label>Email</label>
                                        <div className="input-wrapper">
                                            <span className="input-icon"><Mail size={18} /></span>
                                            <input
                                                type="email"
                                                placeholder="exemple@email.com"
                                                value={Email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                required
                                                className={`${(validationErrors.email || validationErrors.Email) ? 'error-border' : ''} ${shakeEmail ? 'shake-element' : ''}`}
                                            />
                                        </div>
                                        {(validationErrors.email || validationErrors.Email) && <span style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '4px', display: 'block', fontWeight: '500' }}>{getErrorMsg(validationErrors.email || validationErrors.Email)}</span>}
                                    </div>

                                    <div className="premium-input-group">
                                        <label>Mot de passe</label>
                                        <div className="input-wrapper">
                                            <span className="input-icon"><Lock size={18} /></span>
                                            <input
                                                type="password"
                                                placeholder="••••••••"
                                                value={Password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                required
                                                className={`${(validationErrors.password || validationErrors.Password) ? 'error-border' : ''} ${shakePassword ? 'shake-element' : ''}`}
                                            />
                                        </div>
                                        {(validationErrors.password || validationErrors.Password) && <span style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '4px', display: 'block', fontWeight: '500' }}>{getErrorMsg(validationErrors.password || validationErrors.Password)}</span>}
                                    </div>

                                    {!IsLogin && (
                                        <div className="premium-input-group slide-in">
                                            <label>Confirmer le mot de passe</label>
                                            <div className="input-wrapper">
                                                <span className="input-icon"><ShieldCheck size={18} /></span>
                                                <input
                                                    type="password"
                                                    placeholder="••••••••"
                                                    value={ConfirmPS}
                                                    onChange={(e) => setConfirmPS(e.target.value)}
                                                    required
                                                    className={`${validationErrors.confirmPassword ? 'error-border' : ''} ${shakeConfirmPassword ? 'shake-element' : ''}`}
                                                />
                                            </div>
                                            {validationErrors.confirmPassword && <span style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '4px', display: 'block', fontWeight: '500' }}>{getErrorMsg(validationErrors.confirmPassword)}</span>}
                                        </div>
                                    )}

                                    {error && <div className="modern-error-msg"><AlertTriangle className="responsive-icon error-icon" size={18} /> {error}</div>}

                                    {IsLogin && (
                                        <div className="forgot-password-link-wrapper">
                                            <button type="button" onClick={handleInitiateReset} className="btn-text-link-mini">
                                                Mot de passe oublié ?
                                            </button>
                                        </div>
                                    )}

                                    <button type="submit" className="btn-glow-primary">
                                        {IsLogin ? "Se connecter" : "S'inscrire"}
                                    </button>
                                </form>

                                <div className="divider-modern">
                                    <span>Ou</span>
                                </div>
                                <div className="google-auth-wrapper">
                                    <button type="button" onClick={handleGoogleLogin} className="btn-google-modern">
                                        <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" />
                                        <span>{IsLogin ? "Se connecter" : "S'inscrire"} avec Google</span>
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
